import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { EmailCnpjInput } from '../components/EmailCnpjInput';
import { PasswordInput } from '../components/PasswordInput';
import api from '../services/api';
import './Login.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const [loginMode, setLoginMode] = useState<'gov' | 'clinic'>('gov');
  const [emailCnpj, setEmailCnpj] = useState('');
  const [password, setPassword] = useState('');
  const [emailComum, setEmailComum] = useState('');
  const [passwordComum, setPasswordComum] = useState('');
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCallback = useCallback(async (code: string) => {
    try {
      const response = await api.get(`/auth/callback?code=${code}`);
      const { access_token, user } = response.data;
      login(access_token, user);
      navigate('/');
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      alert('Erro ao fazer login. Tente novamente.');
    }
  }, [login, navigate]);

  useEffect(() => {
    // Se já estiver autenticado, redirecionar imediatamente
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    // Verificar se há token no localStorage (pode ter sido salvo pelo callback)
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        login(token, parsedUser);
        // Usar replace para não adicionar ao histórico
        navigate('/', { replace: true });
        return;
      } catch (error) {
        console.error('Erro ao processar dados do localStorage:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    // Verificar se há código de autorização na URL (callback do Gov.br)
    const code = searchParams.get('code');
    if (code) {
      handleCallback(code);
    }
  }, [searchParams, isAuthenticated, navigate, login, handleCallback]);


  const handleGovLogin = async () => {
    setError('');
    setLoading(true);
    try {
      // Verificar se está em modo mock
      const response = await authService.login();
      
      // Se for modo mock, redirecionar para seleção de usuários
      if (response.mock_mode) {
        navigate('/mock-users');
        return;
      }
      
      // Caso contrário, redirecionar para o Gov.br
      window.location.href = response.auth_url;
    } catch (error: any) {
      console.error('Erro ao iniciar login:', error);
      const message = error.userMessage || error.response?.data?.error || 'Erro ao iniciar login. Tente novamente.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClinicLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService.loginWithCpf(emailCnpj, password);
      if (result.user.role !== 'clinica') {
        setError('Acesso negado. Esta área é apenas para clínicas. Use a aba Gov.br para usuários comuns.');
        return;
      }
      login(result.access_token, result.user);
      navigate('/');
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      const message = error.response?.data?.error || 'Erro ao fazer login. Verifique seus dados e tente novamente.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await authService.loginWithCpf(emailComum, passwordComum);
      login(result.access_token, result.user);
      navigate('/');
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      const message = error.response?.data?.error || 'Erro ao fazer login. Verifique email e senha.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>PetID Brasil</h1>
        <p>Sistema de Gerenciamento de Microchips de Animais</p>

        <div className="login-tabs">
          <button
            className={`tab-button ${loginMode === 'gov' ? 'active' : ''}`}
            onClick={() => setLoginMode('gov')}
          >
            Gov.br
          </button>
          <button
            className={`tab-button ${loginMode === 'clinic' ? 'active' : ''}`}
            onClick={() => setLoginMode('clinic')}
          >
            Clínica
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loginMode === 'gov' ? (
          <div className="gov-login">
            <button onClick={handleGovLogin} className="btn btn-primary btn-login" disabled={loading}>
              Entrar com Gov.br
            </button>
            <p className="login-info">
              Use sua conta Gov.br para acessar o sistema
            </p>
            <p className="login-divider">ou</p>
            <Link to="/register" className="btn btn-secondary btn-login" style={{ display: 'block', textAlign: 'center', marginBottom: '12px' }}>
              Registrar (criar conta com email e senha)
            </Link>
            {!showEmailLogin ? (
              <button type="button" className="btn-link" onClick={() => setShowEmailLogin(true)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.9rem', padding: 0 }}>
                Já tem conta? Entrar com email e senha
              </button>
            ) : (
              <form onSubmit={handleEmailLogin} className="login-form" style={{ marginTop: '16px', textAlign: 'left' }}>
                <div className="form-group">
                  <label htmlFor="emailComum">Email</label>
                  <input
                    id="emailComum"
                    type="email"
                    value={emailComum}
                    onChange={(e) => setEmailComum(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    disabled={loading}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="passwordComum">Senha</label>
                  <PasswordInput
                    id="passwordComum"
                    value={passwordComum}
                    onChange={(e) => setPasswordComum(e.target.value)}
                    placeholder="Sua senha"
                    required
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-login" disabled={loading}>
                  {loading ? 'Entrando...' : 'Entrar'}
                </button>
                <button type="button" className="btn-link" onClick={() => setShowEmailLogin(false)} style={{ display: 'block', marginTop: '8px', background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}>
                  Voltar
                </button>
              </form>
            )}
          </div>
        ) : (
          <form onSubmit={handleClinicLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="emailCnpj">Email ou CNPJ</label>
              <EmailCnpjInput
                id="emailCnpj"
                value={emailCnpj}
                onChange={(value) => setEmailCnpj(value)}
                required
                disabled={loading}
              />
              <small className="form-hint">Digite seu email ou CNPJ da clínica</small>
            </div>
            <div className="form-group">
              <label htmlFor="password">Senha</label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                required
                disabled={loading}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-login" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
              <Link to="/admin/forgot-password">Esqueci minha senha</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
